import React, { useState, useEffect, useRef } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { useAuth } from '../state/auth.jsx';
import { Loader2 } from 'lucide-react';

// Initial/fallback data
const initialDashboardData = {
    total_interactions: 0,
    resolved_pct: '0.00',
    unknown_pct: '0.00',
    avg_conversation_time_secs: '0.00',
    hours_saved: '0.00',
    days_counted: 0,
    window: { end: null },
    messagesPerDay: [],
    questionCategories: [
        { name: 'Booking & Stay', value: 4000 },
        { name: 'Amenities', value: 3000 },
        { name: 'Services', value: 2500 },
        { name: 'Local Info', value: 1500 },
        { name: 'Other', value: 1000 },
    ],
    unansweredQuestions: [],
    unansweredKeywords: [],
    mostFrequentQueries: [
        { label: 'WiFi Access Code', value: 85 },
        { label: 'Check-out Process', value: 65 },
        { label: 'Pool & Fitness Hours', value: 50 },
        { label: 'Dining Reservations', value: 40 },
    ],
};

export default function KnowledgeGaps() {
    const { theme, selectedDateRange, customFrom, customTo } = useOutletContext();
    const isLight = theme === 'light';
    const { api, user, tabActiveHotelId } = useAuth();
    const navigate = useNavigate();

    const [dashboardData, setDashboardData] = useState(initialDashboardData);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const chartScrollRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const [dragStartX, setDragStartX] = useState(0);
    const [dragStartScrollLeft, setDragStartScrollLeft] = useState(0);

    // Knowledge gaps state
    const [knowledgeGaps, setKnowledgeGaps] = useState([]);
    const [gapsLoading, setGapsLoading] = useState(false);
    const [selectedQuery, setSelectedQuery] = useState(null);
    const [answer, setAnswer] = useState('');
    const [saving, setSaving] = useState(false);
    const [ignoreId, setIgnoreId] = useState(null);

    const handleChartMouseDown = (event) => {
        if (!chartScrollRef.current) return;
        setIsDragging(true);
        setDragStartX(event.pageX - chartScrollRef.current.offsetLeft);
        setDragStartScrollLeft(chartScrollRef.current.scrollLeft);
    };

    const handleChartMouseMove = (event) => {
        if (!isDragging || !chartScrollRef.current) return;
        event.preventDefault();
        const x = event.pageX - chartScrollRef.current.offsetLeft;
        const walk = (x - dragStartX) * 1.2;
        chartScrollRef.current.scrollLeft = dragStartScrollLeft - walk;
    };

    const stopChartDragging = () => setIsDragging(false);

    // Fetch dashboard stats from API
    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            setError(null);

            // Skip stats request if no property is selected
            if (!tabActiveHotelId) {
                console.log('No tabActiveHotelId found - skipping stats request');
                setDashboardData(initialDashboardData); // Show zeros
                setIsLoading(false);
                return;
            }

            try {
                const isAllMode = tabActiveHotelId === 'all';
                // If custom range provided, send explicit from/to params (YYYY-MM-DD)
                const rangeParams = (customFrom && customTo) ? { from: customFrom, to: customTo } : { selectedDateRange };

                const res = isAllMode
                    ? await api.get('/stats/all', { params: rangeParams })
                    : await api.get('/stats', { params: { ...rangeParams, hotelId: tabActiveHotelId } });
                const stats = res.data?.statistics;
                if (!stats) throw new Error('No statistics in response');
                // Override days_counted using actual messagesPerDay length since remote backend may return wrong value
                stats.days_counted = (stats.messagesPerDay || []).length;
                setDashboardData(stats);
            } catch (e) {
                console.error('Fetch error:', e);
                if (e.response?.status === 401) {
                    localStorage.removeItem('accessToken');
                    navigate('/login');
                    return;
                }
                setError('Failed to fetch dashboard data.');
                setDashboardData(initialDashboardData);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [selectedDateRange, customFrom, customTo, api, navigate, tabActiveHotelId]);

    // Fetch knowledge gaps from API
    useEffect(() => {
        const fetchGaps = async () => {
            if (!tabActiveHotelId || tabActiveHotelId === 'all') {
                setKnowledgeGaps([]);
                return;
            }
            setGapsLoading(true);
            try {
                const res = await api.get(`/knowledge-gaps/${tabActiveHotelId}`);
                if (res.data?.ok) {
                    setKnowledgeGaps(res.data.gaps || []);
                }
            } catch (e) {
                console.error('Failed to fetch knowledge gaps:', e);
                setKnowledgeGaps([]);
            } finally {
                setGapsLoading(false);
            }
        };
        fetchGaps();
    }, [tabActiveHotelId, api]);

    // Resolve a knowledge gap with an official response
    const handleSaveKnowledge = async () => {
        if (!selectedQuery || !answer.trim()) return;
        setSaving(true);
        try {
            const res = await api.post(`/knowledge-gaps/${selectedQuery.id}/resolve`, {
                answer,
                hotelId: tabActiveHotelId
            });
            if (res.data?.ok) {
                setKnowledgeGaps((prev) => prev.filter((g) => g.id !== selectedQuery.id));
                setSelectedQuery(null);
                setAnswer('');
            }
        } catch (e) {
            console.error('Failed to resolve knowledge gap:', e);
        } finally {
            setSaving(false);
        }
    };

    // Ignore a knowledge gap (triggers confirmation)
    const handleIgnore = (id) => {
        if (id) setIgnoreId(id);
    };

    // Confirm ignore
    const confirmIgnore = async () => {
        if (!ignoreId) return;
        setSaving(true);
        try {
            const res = await api.post(`/knowledge-gaps/ignore/${ignoreId}`, {
                hotelId: tabActiveHotelId
            });
            if (res.data?.ok) {
                setKnowledgeGaps((prev) => prev.filter((g) => g.id !== ignoreId));
                if (selectedQuery?.id === ignoreId) {
                    setSelectedQuery(null);
                    setAnswer('');
                }
                setIgnoreId(null);
            }
        } catch (e) {
            console.error('Failed to ignore knowledge gap:', e);
        } finally {
            setSaving(false);
        }
    };

    // Format time in seconds to "Xm Ys" format
    const formatSecondsToMinutesAndSeconds = (totalSeconds) => {
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = Math.floor(totalSeconds % 60);
        return `${minutes}m ${seconds}s`;
    };

    const avgInteractionDurationString = formatSecondsToMinutesAndSeconds(
        parseFloat(dashboardData.avg_conversation_time_secs || 0)
    );

    // Format "calculated on dd-mm-yyyy"
    const formatLastUpdated = (endTime) => {
        if (!endTime) return '';
        const date = new Date(endTime);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
    };

    // Prepare messages per day for chart
    const messagesPerDay = dashboardData.messagesPerDay || [];
    const maxMessages = Math.max(...messagesPerDay.map((d) => d.messages || d.value || 0), 1);

    // Interaction categories for donut
    const interactionCategories = dashboardData.questionCategories || initialDashboardData.questionCategories;
    const totalCategoryValue = interactionCategories.reduce((sum, c) => sum + c.value, 0);

    // Unanswered questions
    const unansweredQuestions = dashboardData.unansweredQuestions || [];
    const unansweredKeywords = dashboardData.unansweredKeywords || [];

    // Most frequent queries
    const mostFrequentQueries = dashboardData.mostFrequentQueries || initialDashboardData.mostFrequentQueries;

    return (
        <section className="h-full flex flex-col overflow-hidden relative">
            {/* Liquid background blobs (dark mode only) */}
            {!isLight && (
                <div className="pointer-events-none absolute inset-0 -z-10">
                    <div className="absolute -top-32 -left-24 w-96 h-96 rounded-full bg-gradient-to-br from-white/6 to-white/0 blur-3xl opacity-70" />
                    <div className="absolute -bottom-40 -right-10 w-[420px] h-[420px] rounded-full bg-gradient-to-tr from-white/4 to-white/0 blur-3xl opacity-60" />
                    <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[520px] h-[520px] rounded-full bg-gradient-to-b from-white/5 to-transparent blur-3xl opacity-40" />
                </div>
            )}

            {/* Content Area */}
            <div className={`flex-1 overflow-y-auto overflow-x-hidden p-4 sm:p-6 space-y-6 pb-32 ${isLight ? 'custom-scroll-light' : 'custom-scroll'}`}>
                {isLoading ? (
                    <div className="flex flex-col items-center justify-center py-20">
                        <Loader2 size={32} className="animate-spin opacity-50 mb-2" />
                        <p className={`text-sm ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>Loading dashboard data...</p>
                    </div>
                ) : error ? (
                    <div className={`p-4 rounded-xl text-sm ${isLight ? 'bg-red-50 text-red-600 border border-red-100' : 'bg-red-900/20 text-red-400 border border-red-800/30'}`}>
                        {error}
                    </div>
                ) : (
                    <>
                        {/* No Properties Notice */}
                        {/* No Properties Notice removed */}


                        {/* KPI Cards Row */}
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
                            <KpiCard
                                label="Interactions"
                                value={dashboardData.total_interactions?.toLocaleString() || '0'}
                                iconUrl="https://i.postimg.cc/xCwKVLKj/Chat-GPT-Image-26-Noe-2025-01-06-07-m-m.png"
                                isLight={isLight}
                            />
                            <KpiCard
                                label="Resolved Rate"
                                value={`${parseFloat(dashboardData.resolved_pct || 0).toFixed(0)}%`}
                                iconUrl="https://i.postimg.cc/6qVGsg6J/Chat-GPT-Image-26-Noe-2025-01-10-54-m-m.png"
                                isLight={isLight}
                            />
                            <KpiCard
                                label="Unknown Info"
                                value={`${parseFloat(dashboardData.unknown_pct || 0).toFixed(0)}%`}
                                iconUrl="https://i.postimg.cc/fRRSF86X/Chat-GPT-Image-26-Noe-2025-01-10-58-m-m.png"
                                isLight={isLight}
                            />
                            <KpiCard
                                label="Avg. Conversation"
                                value={avgInteractionDurationString}
                                iconUrl="https://i.postimg.cc/SsCn83zw/Chat-GPT-Image-26-Noe-2025-01-11-03-m-m.png"
                                isLight={isLight}
                            />
                            <KpiCard
                                label="HOURS SAVED"
                                value={parseFloat(dashboardData.hours_saved || 0).toFixed(2)}
                                iconUrl="https://i.postimg.cc/gkYxXMkp/Chat-GPT-Image-26-Noe-2025-01-11-12-m-m.png"
                                isLight={isLight}
                            />
                        </div>

                        {/* Charts Row */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            {/* Messages Per Day Chart */}
                            <div
                                className={`rounded-3xl backdrop-blur-2xl border p-5 sm:p-6 flex flex-col gap-3 ${isLight
                                    ? 'bg-white border-neutral-200'
                                    : 'bg-white/5 border-white/5'
                                    }`}
                            >
                                <SectionHeader
                                    title="Messages Per Day"
                                    subtitle="How many messages the concierge handles each day."
                                    isLight={isLight}
                                />
                                {/* Scrollable chart wrapper */}
                                <div
                                    ref={chartScrollRef}
                                    className="mt-6 overflow-x-auto chart-scroll"
                                    style={{ WebkitOverflowScrolling: 'touch', cursor: isDragging ? 'grabbing' : 'grab' }}
                                    onMouseDown={handleChartMouseDown}
                                    onMouseMove={handleChartMouseMove}
                                    onMouseLeave={stopChartDragging}
                                    onMouseUp={stopChartDragging}
                                >
                                    <div className="w-auto" style={{ minWidth: messagesPerDay.length <= 7 ? '100%' : `${Math.max(messagesPerDay.length * 28, 360)}px`, height: '160px', position: 'relative' }}>
                                        {messagesPerDay.length > 0 ? (
                                            <>
                                                {/* Bars */}
                                                <div className="absolute inset-x-1 top-8 flex items-end gap-1 opacity-80" style={{ bottom: '40px' }}>
                                                    {messagesPerDay.map((day, idx) => {
                                                        const value = day.messages || day.value || 0;
                                                        const height = 20 + (value / maxMessages) * 60;
                                                        const rawDate = day.date || '';
                                                        return (
                                                            <div
                                                                key={rawDate || idx}
                                                                className={`group relative flex-1 flex flex-col items-center justify-end ${messagesPerDay.length > 7 ? 'sm:!min-w-0' : '!min-w-0'}`}
                                                                style={{ minWidth: messagesPerDay.length > 7 ? '20px' : 'auto' }}
                                                            >
                                                                <div
                                                                    className={`w-full rounded-full ${isLight
                                                                        ? 'bg-gradient-to-t from-neutral-900 to-neutral-600'
                                                                        : 'bg-gradient-to-t from-white/60 to-white'
                                                                        }`}
                                                                    style={{ height: `${height}px` }}
                                                                />
                                                                {/* Hover tooltip */}
                                                                <div
                                                                    className={`pointer-events-none absolute -top-6 px-1.5 py-0.5 rounded-full text-[10px] leading-none whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity z-10 ${isLight ? 'bg-neutral-900 text-white' : 'bg-white text-neutral-900'}`}
                                                                >
                                                                    {value}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                                {/* Axis line */}
                                                <div className={`absolute inset-x-1 h-px ${isLight ? 'bg-neutral-200' : 'bg-white/10'}`} style={{ bottom: '39px' }} />
                                                {/* Date labels — one per bar, rotated */}
                                                <div className="absolute inset-x-1 flex gap-1" style={{ bottom: '0px', height: '38px' }}>
                                                    {messagesPerDay.map((day, idx) => {
                                                        const rawDate = day.date || '';
                                                        const labelInterval = Math.max(1, Math.ceil(messagesPerDay.length / 10));
                                                        const showLabel = messagesPerDay.length <= 14 || idx === 0 || idx === messagesPerDay.length - 1 || idx % labelInterval === 0;

                                                        // Robust date parsing (YYYY-MM-DD or DD-MM-YYYY)
                                                        let label = '';
                                                        if (showLabel && rawDate) {
                                                            let dateObj;
                                                            // Try parsing as ISO first
                                                            dateObj = new Date(rawDate);

                                                            // If ISO fails, try parsing manually (DD-MM-YYYY)
                                                            if (isNaN(dateObj.getTime())) {
                                                                const parts = rawDate.split(/[-/]/);
                                                                if (parts.length === 3) {
                                                                    // Check if YYYY is at the end
                                                                    if (parts[2].length === 4) {
                                                                        dateObj = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
                                                                    } else if (parts[0].length === 4) {
                                                                        dateObj = new Date(`${parts[0]}-${parts[1]}-${parts[2]}`);
                                                                    }
                                                                }
                                                            }

                                                            if (!isNaN(dateObj.getTime())) {
                                                                const dayVal = String(dateObj.getDate()).padStart(2, '0');
                                                                const monthVal = String(dateObj.getMonth() + 1).padStart(2, '0');
                                                                label = `${dayVal}/${monthVal}`;
                                                            } else {
                                                                label = rawDate; // Fallback to raw string
                                                            }
                                                        }

                                                        return (
                                                            <div
                                                                key={rawDate || idx}
                                                                className={`flex-1 flex items-start justify-center overflow-visible ${messagesPerDay.length > 7 ? 'sm:!min-w-0' : '!min-w-0'}`}
                                                                style={{ minWidth: messagesPerDay.length > 7 ? '20px' : 'auto' }}
                                                            >
                                                                {showLabel && (
                                                                    <span
                                                                        className={`text-[10px] font-medium leading-none whitespace-nowrap ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}
                                                                        style={{
                                                                            display: 'inline-block',
                                                                            marginTop: '8px',
                                                                            textAlign: 'center',
                                                                            maxWidth: '44px'
                                                                        }}
                                                                    >
                                                                        {label}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </>
                                        ) : (
                                            <div className={`flex items-center justify-center h-full text-sm ${isLight ? 'text-neutral-400' : 'text-neutral-500'}`}>
                                                No data available
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className={`mt-2 text-[11px] ${isLight ? 'text-neutral-500' : 'text-neutral-500'}`}>
                                    Last {dashboardData.days_counted || 0} days · updated on {formatLastUpdated(dashboardData.window?.end)}
                                </div>
                            </div>

                            {/* Interaction Category Breakdown */}
                            <div
                                className={`rounded-3xl backdrop-blur-2xl border p-5 sm:p-6 flex flex-col gap-4 ${isLight
                                    ? 'bg-white border-neutral-200'
                                    : 'bg-white/5 border-white/5'
                                    }`}
                            >
                                <SectionHeader
                                    title="Interaction Category Breakdown"
                                    subtitle="Distribution of guest inquiries by category."
                                    isLight={isLight}
                                />
                                <div className="relative flex flex-col justify-center">
                                {/* Premium Pill Overlay */}
                                <div className="absolute left-1/2 top-[45%] -translate-x-1/2 -translate-y-1/2 z-20 flex flex-col items-center justify-center">
                                    <div className={`flex items-center gap-2.5 px-4 py-2 rounded-full border shadow-2xl backdrop-blur-xl ${isLight ? 'bg-white/90 border-neutral-200' : 'bg-[#0A0C10]/90 border-white/10'}`}>
                                        <span className={`text-[10px] font-bold uppercase tracking-[0.2em] mt-px ${isLight ? 'text-neutral-800' : 'text-white'}`}>Coming Soon</span>
                                    </div>
                                </div>
                                <div className={`flex flex-col sm:flex-row items-center gap-4 sm:gap-6 mt-4 select-none pointer-events-none transition-all duration-300 ${isLight ? 'blur-[2px] opacity-80' : 'blur-[2px] opacity-70'}`}>
                                    {/* Donut chart */}
                                    <div className="relative h-40 w-40 shrink-0">
                                        <div
                                            className="absolute inset-0 rounded-full shadow-lg"
                                            style={{
                                                background: isLight
                                                    ? `conic-gradient(
                                #E3E3CA 0% 45%,
                                #c7b793 45% 70%,
                                #a3b68a 70% 85%,
                                #5c724a 85% 95%,
                                #354a2f 95% 100%
                              )`
                                                    : `conic-gradient(
                                rgba(227,227,202,0.9) 0% 45%,
                                rgba(199,183,147,0.8) 45% 70%,
                                rgba(163,182,138,0.7) 70% 85%,
                                rgba(92,114,74,0.6) 85% 95%,
                                rgba(53,74,47,0.5) 95% 100%
                              )`,
                                            }}
                                        />
                                        <div
                                            className={`absolute inset-4 rounded-full border ${isLight ? 'bg-neutral-50 border-neutral-200' : 'bg-[#0A0C10] border-white/10'
                                                }`}
                                        />
                                        <div
                                            className={`absolute inset-[38%] rounded-full shadow-inner border flex items-center justify-center ${isLight ? 'bg-neutral-50 border-neutral-200' : 'bg-[#0A0C10] border-white/5'
                                                }`}
                                        >
                                            <span className={`text-sm font-medium tracking-tight ${isLight ? 'text-neutral-900' : 'text-neutral-200'}`}>
                                                100%
                                            </span>
                                        </div>
                                    </div>

                                    {/* Legend */}
                                    <div className="flex-1 space-y-2 text-xs sm:text-sm">
                                        {interactionCategories.map((cat, idx) => (
                                            <LegendRow
                                                key={cat.name}
                                                label={cat.name}
                                                value={`${((cat.value / totalCategoryValue) * 100).toFixed(0)}%`}
                                                isLight={isLight}
                                                colorIndex={idx}
                                            />
                                        ))}
                                    </div>
                                </div>
                                </div>
                            </div>
                        </div>

                        {/* Knowledge Gaps Section */}
                        <div>
                            <h3 className="text-sm sm:text-base font-semibold tracking-tight mb-3">
                                Knowledge Gaps (Unanswered Questions)
                            </h3>

                            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                                {/* Unknown Questions Table */}
                                <div
                                    className={`xl:col-span-2 rounded-3xl backdrop-blur-2xl border overflow-hidden ${isLight
                                        ? 'bg-white border-neutral-200'
                                        : 'bg-white/5 border-white/5'
                                        }`}
                                >
                                    <div className={`p-5 sm:p-6 flex items-center justify-between border-b ${isLight ? 'border-neutral-200' : 'border-white/5'}`}>
                                        <SectionHeader title="Unknown Questions" isLight={isLight} />
                                        <span className={`text-xs px-3 py-1 rounded-full font-medium ${isLight ? 'bg-neutral-100 text-neutral-500' : 'bg-white/5 text-neutral-400'}`}>
                                            {knowledgeGaps.length} Pending
                                        </span>
                                    </div>

                                    {/* Desktop Table View */}
                                    <div className="hidden sm:block overflow-x-auto">
                                        <table className="w-full text-left border-collapse">
                                            <thead className={`text-[10px] uppercase tracking-[0.18em] font-bold ${isLight ? 'text-neutral-400' : 'text-neutral-500'}`}>
                                                <tr>
                                                    <th className="px-6 py-3 text-center">Query Text</th>
                                                    <th className="px-4 py-3 text-center">Frequency</th>
                                                    <th className="px-6 py-3 text-center">Action</th>
                                                </tr>
                                            </thead>
                                            <tbody className={`divide-y ${isLight ? 'divide-neutral-100' : 'divide-white/5'}`}>
                                                {gapsLoading ? (
                                                    <tr>
                                                        <td colSpan="3" className="py-8 text-center">
                                                            <Loader2 size={20} className="animate-spin opacity-50 mx-auto" />
                                                        </td>
                                                    </tr>
                                                ) : knowledgeGaps.length > 0 ? (
                                                    knowledgeGaps.map((item) => (
                                                        <tr
                                                            key={item.id}
                                                            className={`group transition-colors ${selectedQuery?.id === item.id
                                                                ? (isLight ? 'bg-neutral-100' : 'bg-white/10')
                                                                : (isLight ? 'hover:bg-neutral-50' : 'hover:bg-white/[0.03]')
                                                                }`}
                                                        >
                                                            <td className="px-6 py-4">
                                                                <div className="text-sm font-medium">{item.canonical_question}</div>
                                                            </td>
                                                            <td className="px-4 py-4 text-center text-sm font-bold opacity-70">
                                                                {item.frequency}
                                                            </td>
                                                            <td className="px-6 py-4 text-center">
                                                                <div className="flex items-center justify-center gap-3">
                                                                    <button
                                                                        onClick={() => {
                                                                            setSelectedQuery(item);
                                                                            setAnswer('');
                                                                        }}
                                                                        className={`px-4 py-2 rounded-xl text-[10px] font-bold uppercase tracking-widest transition-all ${isLight
                                                                            ? 'bg-neutral-900 text-white hover:bg-neutral-700'
                                                                            : 'bg-white/10 text-white hover:bg-white/20 border border-white/10'
                                                                            }`}
                                                                    >
                                                                        Answer
                                                                    </button>
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleIgnore(item.id);
                                                                        }}
                                                                        className={`p-2 rounded-full transition-all opacity-50 hover:opacity-100 ${isLight ? 'hover:bg-red-50 text-red-500' : 'hover:bg-white/10 text-red-400'}`}
                                                                        title="Ignore question"
                                                                    >
                                                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                                            <line x1="18" y1="6" x2="6" y2="18" />
                                                                            <line x1="6" y1="6" x2="18" y2="18" />
                                                                        </svg>
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="3" className={`p-6 text-center text-sm ${isLight ? 'text-neutral-400' : 'text-neutral-500'}`}>
                                                            No unanswered questions found
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Mobile Card View */}
                                    <div className={`block sm:hidden divide-y ${isLight ? 'divide-neutral-100' : 'divide-white/5'}`}>
                                        {gapsLoading ? (
                                            <div className="py-8 text-center">
                                                <Loader2 size={20} className="animate-spin opacity-50 mx-auto" />
                                            </div>
                                        ) : knowledgeGaps.length > 0 ? (
                                            knowledgeGaps.map((item) => (
                                                <div
                                                    key={item.id}
                                                    className={`p-4 transition-colors ${selectedQuery?.id === item.id
                                                        ? (isLight ? 'bg-neutral-50' : 'bg-white/5')
                                                        : ''
                                                        }`}
                                                >
                                                    <div className="flex flex-col gap-3">
                                                        <div className="text-sm font-medium leading-relaxed">
                                                            {item.canonical_question}
                                                        </div>
                                                        <div className="flex items-center justify-between pt-1">
                                                            <div className={`text-[10px] font-bold uppercase tracking-wider ${isLight ? 'text-neutral-400' : 'text-neutral-500'}`}>
                                                                Freq: <span className={isLight ? 'text-neutral-900' : 'text-white'}>{item.frequency}</span>
                                                            </div>
                                                            <div className="flex items-center gap-2">
                                                                <button
                                                                    onClick={() => {
                                                                        setSelectedQuery(item);
                                                                        setAnswer('');
                                                                    }}
                                                                    className={`px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-widest transition-all ${isLight
                                                                        ? 'bg-neutral-900 text-white active:bg-neutral-700'
                                                                        : 'bg-white/10 text-white active:bg-white/20 border border-white/10'
                                                                        }`}
                                                                >
                                                                    Answer
                                                                </button>
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleIgnore(item.id);
                                                                    }}
                                                                    className={`p-1.5 rounded-full transition-all opacity-50 active:opacity-100 ${isLight ? 'bg-red-50 text-red-500' : 'bg-white/5 text-red-400'}`}
                                                                >
                                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                                        <line x1="18" y1="6" x2="6" y2="18" />
                                                                        <line x1="6" y1="6" x2="18" y2="18" />
                                                                    </svg>
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <div className={`p-6 text-center text-sm ${isLight ? 'text-neutral-400' : 'text-neutral-500'}`}>
                                                No unanswered questions found
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Right Column: Most Frequent Queries */}
                                <div className="space-y-4">
                                    {/* Most Frequent Guest Queries */}
                                    <div
                                        className={`rounded-3xl backdrop-blur-2xl border p-5 sm:p-6 ${isLight
                                            ? 'bg-white border-neutral-200'
                                            : 'bg-white/5 border-white/5'
                                            }`}
                                    >
                                        <SectionHeader
                                            title="Most Frequent Guest Queries"
                                            subtitle="Top topics guests ask your concierge about."
                                            isLight={isLight}
                                        />
                                        <div className="relative flex flex-col justify-center">
                                        {/* Premium Pill Overlay */}
                                        <div className="absolute left-1/2 top-[45%] -translate-x-1/2 -translate-y-1/2 z-20 flex flex-col items-center justify-center">
                                            <div className={`flex items-center gap-2.5 px-4 py-2 rounded-full border shadow-2xl backdrop-blur-xl ${isLight ? 'bg-white/90 border-neutral-200' : 'bg-[#0A0C10]/90 border-white/10'}`}>
                                                <span className={`text-[10px] font-bold uppercase tracking-[0.2em] mt-px ${isLight ? 'text-neutral-800' : 'text-white'}`}>Coming Soon</span>
                                            </div>
                                        </div>
                                        <div className={`space-y-3 mt-3 select-none pointer-events-none transition-all duration-300 ${isLight ? 'blur-[2px] opacity-80' : 'blur-[2px] opacity-70'}`}>
                                            {mostFrequentQueries.map((query, idx) => (
                                                <QueryRow key={idx} label={query.label} value={`${query.value}%`} isLight={isLight} />
                                            ))}
                                        </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* Training Module Modal Popup */}
            {selectedQuery && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    {/* Backdrop */}
                    <div
                        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                        onClick={() => { setSelectedQuery(null); setAnswer(''); }}
                    />
                    {/* Modal */}
                    <div className={`relative w-full max-w-lg rounded-3xl border p-6 sm:p-8 ${isLight
                        ? 'bg-white border-neutral-200 shadow-2xl'
                        : 'bg-white/5 border-white/5 shadow-[0_30px_60px_-15px_rgba(0,0,0,0.7)] backdrop-blur-2xl'
                        }`}>
                        <div className="flex justify-between items-center mb-5">
                            <div className={`w-10 h-10 rounded-2xl flex items-center justify-center text-lg shadow-inner ${isLight ? 'bg-neutral-100' : 'bg-white/10'}`}>🧠</div>
                            <button
                                onClick={() => { setSelectedQuery(null); setAnswer(''); }}
                                className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${isLight ? 'hover:bg-neutral-100 text-neutral-400 hover:text-neutral-900' : 'hover:bg-white/10 text-white/40 hover:text-white'}`}
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                            </button>
                        </div>

                        <h4 className="text-base font-bold mb-1">Training Module</h4>
                        <p className={`text-xs mb-4 leading-relaxed ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
                            Teach the AI how to respond to this question.
                        </p>

                        <div className={`p-4 rounded-2xl mb-4 text-sm italic leading-relaxed border ${isLight ? 'bg-neutral-50 border-neutral-100 text-neutral-600' : 'bg-black/20 border-white/5 text-neutral-300'}`}>
                            "{selectedQuery.canonical_question}"
                        </div>

                        <label className="text-[10px] uppercase font-bold tracking-widest mb-2 block opacity-40">Official Response</label>
                        <textarea
                            rows="5"
                            value={answer}
                            onChange={(e) => setAnswer(e.target.value)}
                            placeholder="Type the official response..."
                            className={`w-full p-4 rounded-2xl text-sm border outline-none transition-all resize-none mb-4 ${isLight ? 'bg-white border-neutral-200 focus:ring-2 ring-neutral-900/5 focus:border-neutral-900' : 'bg-black/30 border-white/10 focus:border-white/30'}`}
                        />

                        <button
                            onClick={handleSaveKnowledge}
                            disabled={!answer.trim() || saving}
                            className={`w-full py-3 rounded-2xl font-bold text-sm tracking-tight transition-all active:scale-95 flex items-center justify-center gap-2 mb-2 ${isLight ? 'bg-neutral-900 text-white hover:bg-neutral-800' : 'bg-white text-black hover:bg-neutral-200 shadow-[0_20px_40px_-10px_rgba(255,255,255,0.2)]'} disabled:opacity-20 disabled:grayscale`}
                        >
                            {saving ? <Loader2 size={16} className="animate-spin" /> : null}
                            Save Knowledge
                        </button>
                    </div>
                </div>
            )}

            {/* Ignore Confirmation Modal */}
            {ignoreId && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
                    <div
                        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                        onClick={() => !saving && setIgnoreId(null)}
                    />
                    <div className={`relative w-full max-w-sm rounded-3xl border p-6 sm:p-8 animate-in fade-in zoom-in-95 duration-200 ${isLight
                        ? 'bg-white border-neutral-200 shadow-2xl'
                        : 'bg-white/5 border-white/5 shadow-[0_30px_60px_-15px_rgba(0,0,0,0.7)] backdrop-blur-2xl'
                        }`}>
                        <h4 className="text-base font-bold mb-2">Ignore Question?</h4>
                        <p className={`text-sm mb-6 ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
                            Do you want to ignore this question? It will be removed from the list.
                        </p>
                        <div className="flex gap-3">
                            <button
                                onClick={() => setIgnoreId(null)}
                                disabled={saving}
                                className={`flex-1 py-3 rounded-2xl font-medium text-sm transition-all ${isLight
                                    ? 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                                    : 'bg-white/5 text-neutral-400 hover:bg-white/10'
                                    }`}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmIgnore}
                                disabled={saving}
                                className={`flex-1 py-3 rounded-2xl font-bold text-sm transition-all text-white ${saving ? 'opacity-50' : 'hover:bg-red-600'} bg-red-500 shadow-lg shadow-red-500/20 flex items-center justify-center gap-2`}
                            >
                                {saving && <Loader2 size={14} className="animate-spin" />}
                                {saving ? 'Ignoring...' : 'Yes, Ignore'}
                            </button>
                        </div>
                    </div>
                </div>
            )}


            {/* Custom scroll styles */}
            <style>{`
        .custom-scroll::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }
        .custom-scroll::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scroll::-webkit-scrollbar-thumb {
          background-color: rgba(128, 128, 128, 0.3);
          border-radius: 20px;
        }
        .chart-scroll {
          scrollbar-width: thin;
          scrollbar-color: rgba(148, 148, 148, 0.75) rgba(148, 148, 148, 0.12);
        }
        .chart-scroll::-webkit-scrollbar {
          height: 8px;
        }
        .chart-scroll::-webkit-scrollbar-track {
          background: rgba(148, 148, 148, 0.08);
          border-radius: 9999px;
        }
        .chart-scroll::-webkit-scrollbar-thumb {
          background-color: rgba(148, 148, 148, 0.55);
          border-radius: 9999px;
          min-height: 16px;
        }
        .chart-scroll::-webkit-scrollbar-thumb:hover {
          background-color: rgba(148, 148, 148, 0.75);
        }
      `}</style>
        </section >
    );
}

/* ---------- Reusable components ---------- */

function KpiCard({ label, value, iconUrl, isLight }) {
    return (
        <div
            className={`rounded-3xl backdrop-blur-2xl px-4 py-3 flex flex-col gap-3 ${isLight
                ? 'bg-white shadow-sm'
                : 'bg-white/5'
                }`}
        >
            <div className="flex items-center justify-between">
                <div className={`text-[11px] uppercase tracking-[0.18em] ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
                    {label}
                </div>
                {iconUrl && (
                    <div className={`h-7 w-7 rounded-2xl flex items-center justify-center border ${isLight ? 'bg-neutral-50 border-neutral-200' : 'bg-white/5 border-white/15'}`}>
                        <img src={iconUrl} alt="" className="h-6 w-6 object-contain" />
                    </div>
                )}
            </div>
            <div className="text-xl font-semibold tracking-tight">{value}</div>
        </div>
    );
}

function SectionHeader({ title, subtitle, isLight }) {
    return (
        <div>
            <h4 className="text-sm sm:text-base font-semibold tracking-tight">{title}</h4>
            {subtitle && (
                <p className={`mt-1 text-[10px] sm:text-xs ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
                    {subtitle}
                </p>
            )}
        </div>
    );
}

function QueryRow({ label, value, isLight }) {
    return (
        <div className="flex items-center gap-3 text-xs sm:text-sm">
            <div className={`flex-1 truncate ${isLight ? 'text-neutral-900' : 'text-neutral-200'}`}>
                {label}
            </div>
            <div className="flex-1">
                <div className={`h-2 rounded-full overflow-hidden ${isLight ? 'bg-neutral-200' : 'bg-white/5'}`}>
                    <div
                        className={`h-full rounded-full transition-all ${isLight
                            ? 'bg-gradient-to-r from-neutral-500 to-neutral-900'
                            : 'bg-gradient-to-r from-white/40 to-white/80'
                            }`}
                        style={{ width: value }}
                    />
                </div>
            </div>
            <div className={`w-10 text-right text-[11px] ${isLight ? 'text-neutral-700' : 'text-neutral-300'}`}>
                {value}
            </div>
        </div>
    );
}

function LegendRow({ label, value, isLight, colorIndex }) {
    const colorsLight = ['#E3E3CA', '#c7b793', '#a3b68a', '#5c724a', '#354a2f'];
    const colorsDark = [
        'rgba(227,227,202,0.9)',
        'rgba(199,183,147,0.8)',
        'rgba(163,182,138,0.7)',
        'rgba(92,114,74,0.6)',
        'rgba(53,74,47,0.5)',
    ];
    const dotColor = isLight ? colorsLight[colorIndex % 5] : colorsDark[colorIndex % 5];

    return (
        <div className={`flex items-center justify-between gap-3 ${isLight ? 'text-neutral-800' : 'text-neutral-300'}`}>
            <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full" style={{ background: dotColor }} />
                <span className="text-xs sm:text-sm">{label}</span>
            </div>
            <span className={`text-xs sm:text-sm ${isLight ? 'text-neutral-600' : 'text-neutral-400'}`}>
                {value}
            </span>
        </div>
    );
}

function TableRow({ query, freq, last, status, isLight }) {
    let toneClasses;
    if (status === 'New') {
        toneClasses = isLight
            ? 'bg-red-50 text-red-700 border-red-100'
            : 'bg-white/8 text-red-100 border-white/10';
    } else if (status === 'Knowledge Added' || status === 'Knowledge added') {
        toneClasses = isLight
            ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
            : 'bg-white/8 text-emerald-100 border-white/10';
    } else {
        toneClasses = isLight
            ? 'bg-amber-50 text-amber-700 border-amber-100'
            : 'bg-white/8 text-amber-100 border-white/10';
    }

    return (
        <>
            {/* Desktop row */}
            <div
                className={`hidden sm:grid grid-cols-12 px-4 py-3 text-xs border-t transition ${isLight
                    ? 'text-neutral-900 border-neutral-200 hover:bg-neutral-50'
                    : 'text-neutral-200 border-white/5 hover:bg-white/[0.02]'
                    }`}
            >
                <div className="col-span-6 pr-4 truncate">{query}</div>
                <div className={`col-span-2 text-right ${isLight ? 'text-neutral-700' : 'text-neutral-300'}`}>
                    {freq}
                </div>
                <div className={`col-span-2 text-right ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
                    {last}
                </div>
                <div className="col-span-2 text-right">
                    <span className={`inline-flex items-center justify-end px-2 py-0.5 rounded-full border text-[10px] ${toneClasses}`}>
                        {status}
                    </span>
                </div>
            </div>

            {/* Mobile card */}
            <div
                className={`sm:hidden p-3 border-t ${isLight ? 'border-neutral-200' : 'border-white/5'
                    }`}
            >
                <p className={`text-xs mb-2 ${isLight ? 'text-neutral-900' : 'text-neutral-200'}`}>{query}</p>
                <div className="flex items-center justify-between text-[11px]">
                    <div className={`${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
                        Freq: {freq} · {last}
                    </div>
                    <span className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] ${toneClasses}`}>
                        {status}
                    </span>
                </div>
            </div>
        </>
    );
}
